import React, { useEffect, useRef, useState } from 'react';
import { getIcon ,useTheme} from '@ohif/ui'
import { capitalizeWords } from '@custom/Utils/utils';


const AssignTelePathologistModal = ({ isOpen, onClose, title = "Assign", subtitle = "Users", patientName, userData = [], isLoading, onAssign,rejectedPathologists = []  }) => {
  const { isDarkMode } = useTheme();
  const [selectedRadiologists, setSelectedRadiologists] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const selectRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      // Check if click is outside the select box & dropdown
      if (
        selectRef.current && !selectRef.current.contains(event.target) &&
        dropdownRef.current && !dropdownRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
    }

    // Add event listener when dropdown is open
    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      // Cleanup event listener when component unmounts or dropdown closes
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleSelectClick = () => {
    setIsDropdownOpen((prev) => !prev);
  };


  const handleRowClick = (doctorId) => {
    setSelectedRadiologists([doctorId]);
    setIsDropdownOpen(false); // Close dropdown after selection
  };

  const formatAvailability = (days) => {
    const dayOrder = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    const areDaysConsecutive = days.every((day, index) => {
      const dayIndex = dayOrder.indexOf(day);
      return index === 0 || dayIndex === dayOrder.indexOf(days[index - 1]) + 1;
    });

    return days.length > 2 && areDaysConsecutive
      ? `${days[0]} - ${days[days.length - 1]}`
      : days.length > 1
        ? days.join(", ")
        : days[0];
  };

  const formatTimeAvailability = (fromTime, toTime) => {
    const convertToAmPm = (time) => {
      const [hours, minutes] = time.split(":");
      const date = new Date();
      date.setHours(Number(hours), Number(minutes));
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
    };
    return `${convertToAmPm(fromTime)} - ${convertToAmPm(toTime)}`;
  };


  const formatSelectedValues = () => {
    if (selectedRadiologists.length === 0) return "-Select-";

    const selectedDoctor = userData.find(doctor => doctor._id === selectedRadiologists[0]);
    return selectedDoctor ? selectedDoctor.name : "-Select-";
  };

  const isPathologistRejected = (pathologistId) => {
    return rejectedPathologists.includes(pathologistId);
  };
  if (!isOpen) return null; // Hide modal when not open

  return (
    <div className="modal-overlay fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center" style={{ zIndex: 9999 }}>
      <div className="modal-container w-[32rem] bg-white p-6 rounded-lg shadow-lg radiologist-popup">
        {/* Modal Header */}
        <div className="modal-header flex justify-between items-center">
          <h2 className="text-xl font-semibold">{title}</h2>
          <button onClick={onClose} className="icon icon-header"  style={isDarkMode ? { filter: "invert(1)"} : {}}>
            {getIcon("CrossIcon", { height: "16px", width: "16px" })}
          </button>
        </div>

        {/* Modal Content */}
        <p className="font-semibold">
          Patient Name: <span className="font-medium text-black" style={isDarkMode ? { color: "white" } : {color : "black"}}> {capitalizeWords(patientName)}</span>
        </p>

        <label className="block text-md font-medium">{subtitle}</label>
        <div className="relative-div">
          <div ref={selectRef} className="custom-select block w-full border border-border rounded-md p-2 mb-4 cursor-pointer" onClick={handleSelectClick}>
            <span className={selectedRadiologists.length > 0 ? isDarkMode ?  "text-white" : "text-black" : ""}>{formatSelectedValues()}</span>
          </div>

          {isDropdownOpen && (
            <div ref={dropdownRef} className="absolute dropdown-container z-10 bg-white border border-border rounded-md max-h-60 overflow-y-auto pb-3">
              <table className="w-full border-collapse">
                <thead>
                  <tr className={isDarkMode ? "bg-black": "bg-gray-200"}>
                    <th className="border p-2 px-8 text-base font-medium text-left">Name</th>
                    <th className="border p-2 px-3 text-base font-medium text-left">Time Slots</th>
                    <th className="border p-2 px-3 text-base font-medium text-left">Availability (Days)</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={3} className="text-center py-2">Loading...</td>
                    </tr>
                  ) : (
                    userData.map((doctor) =>
                    {
                      const isRejected = isPathologistRejected(doctor._id);
                      return( <tr
                        key={doctor._id}
                        className={`cursor-pointer ${isDarkMode ? "text-white hover:bg-black" : "text-black hover:bg-gray-100"} ${
                          selectedRadiologists.includes(doctor._id) ? "bg-common-bright text-black" : isRejected ? "bg-red-600 text-white " : ""
                        }`}
                        style={{background: isRejected ? '#dc2f2f':""}}
                          onClick={() => !isRejected && handleRowClick(doctor._id)}
                      >
                        <td className="p-1 px-5 text-sm flex items-center">
                          {selectedRadiologists.includes(doctor._id) && <span className="check-icon">{getIcon("CheckIcon")}</span>}
                          <span className="ml-3">{doctor.name}</span>
                        </td>
                        <td className="p-1 px-7 text-sm w-[5.5rem]" >
                          <span className="ml-3">
                            {doctor?.fromTimeAvailability && doctor?.toTimeAvailability
                              ? formatTimeAvailability(doctor?.fromTimeAvailability, doctor?.toTimeAvailability)
                              : "--"}
                          </span>
                        </td>
                        <td className="p-1 px-5 text-sm" style={{display: "flex", flexWrap: "wrap", width: "10rem"}}>
                          <span className="ml-3">{doctor.availabilityDay ? formatAvailability(doctor.availabilityDay) : "--"}</span>
                        </td>
                      </tr>)
                    }
                    )
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal Buttons */}
        <div className="btn__group mt-4 flex justify-end">
          <button className="btn btn__white" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn__primary" onClick={() => onAssign(selectedRadiologists)}>
            Assign
          </button>
        </div>
      </div>
    </div>
  )
}

export default AssignTelePathologistModal
